/* @flow */
import * as React from 'react';
import { capitalize } from 'lodash';

import CurrentUser from 'auth/CurrentUser';
import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemTitle,
} from 'components/Accordion';
import Avatar from 'components/Avatar.jsx';
import AvatarWithText from 'components/AvatarWithText';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import Glyph from 'components/Glyph.jsx';
import GroupAvatarWithText from 'groups/components/GroupAvatarWithText';
import Heading from 'components/Heading.jsx';
import Icon from 'components/Icon.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import Tooltip from 'components/Tooltip.jsx';
import TooltipTrigger from 'components/TooltipTrigger.jsx';
import GroupMock from 'entities/mocks/GroupMock';
import PhoneCallButton from 'phoneCalls/components/PhoneCallButton.jsx';
// import StartChatButton from 'user/components/StartChatButton.jsx';
// import UserActionsMenuButton from 'user/components/UserActionsMenuButton.jsx';
import UserAvatar from 'users/components/UserAvatar.jsx';

import './PeopleListUserProfile.scss';

type PeopleListUserProfileSectionProps = {
  children?: any,
  count?: number,
  disabled?: boolean,
  title: string,
};

function PeopleListUserProfileSection({
  children,
  count = 0,
  disabled,
  title,
  ...others
}: PeopleListUserProfileSectionProps) {
  return (
    <Row size="small">
      <AccordionItem
        {...others}
        className={!children && 'PeopleListUserProfile-accordion-item--disabled'}>
        <AccordionItemTitle>
          <Heading flush level="3">
            <Text color={disabled || !children ? 'eclipse' : 'inherit'}>
              {title} ({count})
            </Text>
          </Heading>
        </AccordionItemTitle>
        <AccordionItemBody>
          {children && <div className="PeopleListUserProfile-accordion-item-body">{children}</div>}
        </AccordionItemBody>
      </AccordionItem>
    </Row>
  );
}

export default function PeopleListUserProfile() {
  // TODO: placeholder data
  const group = GroupMock.get();
  const user = CurrentUser.getUser();
  const userJSON = user.toJSON();
  const userIsRostered = true;

  const {
    default_phone_number: phoneNumber,
    display_name: name,
    email,
    grade_level: gradeLevel,
    role,
    uuid,
  } = userJSON;

  let subtext;

  if (role && gradeLevel) {
    subtext = `${capitalize(role)}, ${gradeLevel}`;
  } else if (role) {
    subtext = capitalize(role);
  } else if (gradeLevel) {
    subtext = gradeLevel;
  }

  const groupListItem = <GroupAvatarWithText avatarSize="small" group={group} hideCode />;

  return (
    <aside className="PeopleListUserProfile">
      <Row size="large">
        <Text align="center" element="div">
          <Row size="small">
            <div className="PeopleListUserProfile-avatar">
              <UserAvatar size="xxlarge" user={userJSON} />
              <TooltipTrigger
                placement="top"
                tooltip={<Tooltip>{userIsRostered ? 'Rostered' : 'Not rostered'}</Tooltip>}>
                <span className="PeopleListUserProfile-rosterStatus">
                  <Icon
                    color={userIsRostered ? 'shamrock' : 'koala'}
                    name="link-linked"
                    size="xlarge"
                  />
                </span>
              </TooltipTrigger>
            </div>
          </Row>
          <Text element="h2" size="xlarge" truncate>
            {name}
          </Text>
          <Text color="thunder" size="medium">
            {subtext}
          </Text>
        </Text>
      </Row>
      <Row size="large">
        {/*
        TODO:
        • StartChatButton
        • UserActionsMenuButton
        */}
        <Buttons>
          <Button>Send message</Button>
          {user.hasFeature('phone_calls') && (
            <PhoneCallButton calleeName={name} calleeUuid={uuid} size="small" />
          )}
          <Button level="secondary" aria-label="TODO More options">
            <Glyph name="ellipses" solo />
          </Button>
        </Buttons>
      </Row>
      <Accordion accordion={false}>
        <PeopleListUserProfileSection title="Contact information" count={2} expanded>
          <AvatarWithText
            avatar={<Avatar glyphName="email-stroke" />}
            subtext="Email"
            title={email}
          />
          {phoneNumber && (
            <AvatarWithText
              avatar={<Avatar glyphName="phone" />}
              subtext="Phone"
              title={phoneNumber.pretty}
            />
          )}
        </PeopleListUserProfileSection>
        <PeopleListUserProfileSection title="Family members" count={2}>
          <AvatarWithText
            avatar={<UserAvatar user={userJSON} />}
            subtext={capitalize(role)}
            title={name}
          />
        </PeopleListUserProfileSection>
        <PeopleListUserProfileSection title="Classes owned" />
        <PeopleListUserProfileSection title="Classes joined" count={2}>
          {groupListItem}
        </PeopleListUserProfileSection>
      </Accordion>
    </aside>
  );
}
